/**
 * The workspace files Apiboo itself just wrote — so the file watcher can tell
 * its own flush from an edit made in VS Code or by `git pull`.
 *
 * Before this (git-sync audit 01.10.2026) every flush came straight back in:
 * the watcher saw the app's own YAML, `scheduleExternalSnapshotApply`
 * diff-applied it to SQLite, and whatever the serializer did not carry was
 * deleted from the local data (and queued to the cloud as `unset`) within a
 * second of every save. Card 4's rule: ignore Apiboo's own writes.
 *
 * Content-based, not time-based: an event is an echo only when the file on
 * disk is EXACTLY what the flush wrote (or still absent after a flush deleted
 * it). A user edit — even one landing in the same instant — has different
 * bytes and goes through.
 */

/** normalized absolute path → the content the flush wrote, or null for a delete */
const own = new Map();

/** Forward slashes, no trailing slash, lower-case drive letter (C:/ vs c:/ from different APIs). */
export function normalizeAbsPath(path) {
  const p = String(path || '').replace(/\\/g, '/').replace(/\/+$/, '');
  return /^[A-Za-z]:\//.test(p) ? p[0].toLowerCase() + p.slice(1) : p;
}

export function recordOwnWrite(absPath, content) {
  own.set(normalizeAbsPath(absPath), String(content));
}

export function recordOwnDelete(absPath) {
  own.set(normalizeAbsPath(absPath), null);
}

/**
 * Is this watcher event our own flush coming back?
 * @param {string} absPath
 * @param {(path: string) => Promise<string | null>} readText  null when the file does not exist
 */
export async function isOwnEcho(absPath, readText) {
  const key = normalizeAbsPath(absPath);
  if (!own.has(key)) return false;
  let current;
  try {
    current = await readText(absPath);
  } catch {
    current = null;
  }
  return own.get(key) === (current == null ? null : String(current));
}

/** Tests only. */
export function __resetOwnWritesForTests() {
  own.clear();
}
